import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Mail, MessageSquare, Bell, Smartphone, Info } from "lucide-react";

const CHANNELS = [
  { key: "email", icon: Mail, label: "Email", desc: "Booking confirmations, receipts, and important updates. Powered by Resend.", pending: true },
  { key: "sms", icon: Smartphone, label: "SMS", desc: "Critical booking alerts to your phone. Powered by Twilio.", pending: true },
  { key: "whatsapp", icon: MessageSquare, label: "WhatsApp", desc: "Rich booking updates on WhatsApp. Powered by Twilio.", pending: true },
  { key: "in_app", icon: Bell, label: "In-app", desc: "Real-time notifications inside SwaanStay. Always on.", pending: false },
];

export default function Settings() {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/notification-prefs").then(({ data }) => setPrefs(data)); }, []);

  const save = async (next) => {
    setPrefs(next);
    setSaving(true);
    try {
      await api.post("/notification-prefs", next);
      toast.success("Preferences saved");
    } catch {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  if (!prefs) return <div className="p-10"><span className="hw-pulse-dot" /></div>;

  return (
    <div className="bg-slate-50 min-h-[calc(100vh-4rem)]">
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-10">
        <div className="hw-label text-blue-700">Account</div>
        <h1 className="hw-head text-4xl md:text-5xl font-black text-slate-900 mt-3">Settings</h1>
        <p className="text-slate-600 mt-2">Choose how SwaanStay reaches you.</p>

        <div className="mt-8 hw-card p-6" data-testid="notification-prefs-card">
          <h2 className="hw-head text-2xl font-black text-slate-900">Notification channels</h2>
          <p className="text-sm text-slate-500 mt-1">In-app is always on. Other channels turn on the moment the provider key is added.</p>

          <div className="mt-6 space-y-4">
            {CHANNELS.map((c) => (
              <div key={c.key} className="flex items-start justify-between gap-4 border-b border-slate-100 pb-4 last:border-0 last:pb-0" data-testid={`pref-row-${c.key}`}>
                <div className="flex items-start gap-3 flex-1">
                  <div className="w-10 h-10 rounded-md bg-blue-50 grid place-items-center flex-shrink-0"><c.icon className="w-5 h-5 text-blue-700" /></div>
                  <div>
                    <div className="hw-head font-bold text-slate-900 flex items-center gap-2">
                      {c.label}
                      {c.pending && <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded-sm">Activating</span>}
                    </div>
                    <div className="text-sm text-slate-600 mt-0.5">{c.desc}</div>
                  </div>
                </div>
                <Switch
                  checked={!!prefs[c.key]}
                  disabled={c.key === "in_app" || saving}
                  onCheckedChange={(v) => save({ ...prefs, [c.key]: v })}
                  data-testid={`pref-toggle-${c.key}`}
                />
              </div>
            ))}
          </div>

          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-md p-3 text-xs text-blue-800 flex items-start gap-2" data-testid="pref-info">
            <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>Your preferences are saved and applied automatically once external providers (Twilio SMS/WhatsApp, Resend email) are activated. No action needed on your side.</span>
          </div>
        </div>
      </div>
    </div>
  );
}
